import { useState, useEffect } from "react";
import axios from "axios";
import { Star, Send, Check, Heart } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  ar: {
    title: "كيف كانت تجربتك مع الاقتراح؟",
    subtitle: "تقييمك يساعدنا على تحسين دقّة اقتراحات التسعير للمستقلين السعوديين.",
    standaloneTitle: "شاركنا رأيك واقتراحاتك",
    standaloneSubtitle: "رأيك يهمنا — سواء جربت الحاسبة أو عندك اقتراح لتحسين المنصة. أرسل تقييمك واقتراحك الآن.",
    starLabels: ["سيّئ", "مقبول", "جيد", "جيد جدًا", "ممتاز"],
    placeholder: "اكتب اقتراحًا أو تعليقًا (اختياري)…",
    namePlaceholder: "اسمك (اختياري)",
    nameHint: "اتركه فارغًا لإرسال التقييم كـ \"زائر\"",
    submit: "إرسال التقييم",
    submitting: "جارٍ الإرسال…",
    thanks: "شكرًا لك! تم استلام تقييمك ❤️",
    thanksSub: "اقتراحاتك تصل مباشرة لفريق برولانسر.",
    submitAnother: "إرسال تقييم آخر",
    skipped: "لاحقًا",
    rate: "اضغط النجوم للتقييم",
    requireRating: "اختر عدد النجوم أولًا",
    guestTag: "تقييم من زائر",
    dir: "rtl",
    font: "font-arabic",
  },
  en: {
    title: "How was your AI suggestion?",
    subtitle: "Your rating helps us improve pricing accuracy for Saudi freelancers.",
    standaloneTitle: "Share your feedback & suggestions",
    standaloneSubtitle: "Your opinion matters — whether you tried the calculator or have an idea to improve the platform. Send your rating and suggestion now.",
    starLabels: ["Poor", "Fair", "Good", "Very good", "Excellent"],
    placeholder: "Add a quick comment or suggestion (optional)…",
    namePlaceholder: "Your name (optional)",
    nameHint: "Leave empty to submit as \"Guest\"",
    submit: "Submit rating",
    submitting: "Sending…",
    thanks: "Thanks for your feedback ❤️",
    thanksSub: "Your suggestions reach the Prolancer team directly.",
    submitAnother: "Submit another rating",
    skipped: "Later",
    rate: "Tap a star to rate",
    requireRating: "Please pick a rating first",
    guestTag: "Guest review",
    dir: "ltr",
    font: "font-outfit",
  },
};

export default function RatingFeedback({ lang = "ar", theme = "dark", context = "ai_suggestion", serviceType = "", email = "", standalone = false, compact = false }) {
  const dark = theme === "dark";
  const L = lang === "ar" ? T.ar : T.en;
  const storageKey = `pl_feedback_${context}`;
  const [visible, setVisible] = useState(true);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState(() => localStorage.getItem("pl_name") || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const isGuest = !name.trim() && !email;
  const displayTitle = standalone ? L.standaloneTitle : L.title;
  const displaySubtitle = standalone ? L.standaloneSubtitle : L.subtitle;

  useEffect(() => {
    // In standalone mode, the form is always-on — never auto-hide across sessions.
    if (standalone) return;
    // Hide only if explicitly dismissed within the last hour on this device.
    try {
      const last = localStorage.getItem(storageKey + "_dismissed");
      if (last && Date.now() - Number(last) < 1 * 3600 * 1000) {
        setVisible(false);
      }
    } catch {}
  }, [storageKey, standalone]);

  if (!visible) return null;

  const submit = async () => {
    if (!rating) {
      toast.error(L.requireRating);
      return;
    }
    setSubmitting(true);
    try {
      const trimmedName = name.trim();
      if (trimmedName) localStorage.setItem("pl_name", trimmedName);
      await axios.post(`${API}/feedback`, {
        rating,
        comment: comment.trim(),
        name: trimmedName,
        context,
        service_type: serviceType,
        email,
        language: lang,
      });
      localStorage.setItem(storageKey, String(Date.now()));
      setDone(true);
      toast.success(L.thanks);
    } catch {
      toast.error(lang === "ar" ? "تعذّر الإرسال — حاول لاحقًا" : "Failed to send — try later");
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(storageKey + "_dismissed", String(Date.now()));
    setVisible(false);
  };

  if (done) {
    return (
      <div
        data-testid="feedback-thanks"
        dir={L.dir}
        className={`mt-5 rounded-2xl border p-5 flex items-center gap-3 ${
          dark ? "bg-[#18c98f]/10 border-[#18c98f]/40" : "bg-[#18c98f]/10 border-[#12a777]/40"
        }`}
      >
        <div className="h-10 w-10 rounded-full bg-[#18c98f] text-white inline-flex items-center justify-center shrink-0">
          <Heart className="h-5 w-5" />
        </div>
        <div className={`${L.font} flex-1 min-w-0`}>
          <div className={`font-bold ${dark ? "text-white" : "text-[#182138]"}`}>{L.thanks}</div>
          <div className={`text-xs mt-0.5 ${dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>{L.thanksSub}</div>
        </div>
        {standalone && (
          <button
            data-testid="feedback-submit-another"
            onClick={() => {
              setDone(false);
              setRating(5);
              setHover(0);
              setComment("");
            }}
            className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold inline-flex items-center gap-1.5 ${
              dark
                ? "bg-[#18c98f] text-white hover:brightness-110"
                : "bg-[#12a777] text-white hover:brightness-110"
            } ${L.font}`}
          >
            {L.submitAnother}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="rating-feedback"
      dir={L.dir}
      className={
        compact
          ? "mt-2"
          : `mt-5 rounded-2xl border p-5 ${dark ? "bg-[#1f2a47] border-[#2e3a5c]" : "bg-[#f3f8ff] border-[#e5e5ea]"}`
      }
    >
      {!compact && (
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl inline-flex items-center justify-center bg-[#18c98f]/15 text-[#18c98f] shrink-0">
          <Star className="h-5 w-5 fill-current" />
        </div>
        <div className={`flex-1 min-w-0 ${L.font}`}>
          <div className={`font-bold text-sm sm:text-base ${dark ? "text-white" : "text-[#182138]"}`}>
            {displayTitle}
          </div>
          <div className={`text-xs mt-1 ${dark ? "text-white/60" : "text-[#2d2c35]/70"}`}>
            {displaySubtitle}
          </div>
        </div>
        {!standalone && (
          <button
            data-testid="feedback-dismiss"
            onClick={dismiss}
            className={`text-[11px] shrink-0 ${dark ? "text-white/50 hover:text-white/80" : "text-[#2d2c35]/50 hover:text-[#2d2c35]"}`}
          >
            {L.skipped}
          </button>
        )}
      </div>
      )}

      {/* Stars */}
      <div className="mt-4 flex items-center justify-center gap-1.5" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => {
          const active = (hover || rating) >= n;
          return (
            <button
              key={n}
              data-testid={`feedback-star-${n}`}
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              className="h-11 w-11 inline-flex items-center justify-center rounded-xl transition-transform hover:scale-110 active:scale-95"
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
            >
              <Star
                className={`h-7 w-7 transition-colors ${
                  active ? "fill-[#18c98f] text-[#18c98f]" : dark ? "text-white/25" : "text-[#2d2c35]/25"
                }`}
                strokeWidth={1.5}
              />
            </button>
          );
        })}
      </div>
      <div className={`mt-1 text-center text-[11px] ${dark ? "text-white/60" : "text-[#2d2c35]/60"} ${L.font}`}>
        {rating > 0 ? L.starLabels[rating - 1] : L.rate}
      </div>

      {/* Name field */}
      <input
        data-testid="feedback-name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={L.namePlaceholder}
        maxLength={100}
        className={`mt-4 w-full px-3 py-2 rounded-xl text-sm ${
          dark
            ? "bg-[#182138] border border-[#2e3a5c] text-white placeholder:text-white/40 focus:border-[#18c98f]"
            : "bg-white border border-[#e5e5ea] text-[#2d2c35] placeholder:text-[#2d2c35]/40 focus:border-[#18c98f]"
        } focus:outline-none ${L.font}`}
      />
      <div className={`mt-1.5 flex items-center justify-between text-[11px] ${dark ? "text-white/50" : "text-[#2d2c35]/50"} ${L.font}`}>
        <span>{L.nameHint}</span>
        {isGuest && (
          <span
            data-testid="guest-tag"
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
              dark ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "bg-amber-100 text-amber-800 border border-amber-200"
            }`}
          >
            {L.guestTag}
          </span>
        )}
      </div>

      {/* Comment textarea */}
      <textarea
        data-testid="feedback-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={L.placeholder}
        maxLength={1000}
        rows={2}
        className={`mt-4 w-full px-3 py-2 rounded-xl text-sm resize-none ${
          dark
            ? "bg-[#182138] border border-[#2e3a5c] text-white placeholder:text-white/40 focus:border-[#18c98f]"
            : "bg-white border border-[#e5e5ea] text-[#2d2c35] placeholder:text-[#2d2c35]/40 focus:border-[#18c98f]"
        } focus:outline-none ${L.font}`}
      />

      {/* Submit button */}
      <button
        data-testid="feedback-submit"
        onClick={submit}
        disabled={submitting || !rating}
        className={`mt-3 w-full h-11 rounded-full font-bold text-sm inline-flex items-center justify-center gap-2 transition-all ${
          !rating
            ? dark
              ? "bg-[#2e3a5c] text-white/40 cursor-not-allowed"
              : "bg-[#e5e5ea] text-[#2d2c35]/40 cursor-not-allowed"
            : "bg-[#18c98f] text-white hover:brightness-110 shadow-[0_6px_16px_-6px_rgba(24,201,143,0.6)]"
        } ${L.font}`}
      >
        {submitting ? (
          <>
            <Send className="h-4 w-4 animate-pulse" />
            {L.submitting}
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            {L.submit}
          </>
        )}
      </button>
    </div>
  );
}
